import React, { useState, useEffect } from "react";
import "./../App.scss";
import { Branding, StopName, StopDirection, ControlPanel } from "./../Header/Header";
import { StopPredictions } from "./Tracker/StopPredictions";
import Footer from "./../Footer/Footer";
import {
    getStopInfo,
    getStopRoutes,
    getRouteColours,
    getStopPredictions,
} from "./../API/API";
import LoadingScreen from "./../Utilities/LoadingScreen";
import useInterval from "./../Utilities/useInterval";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import ConnectionError from "./../Utilities/ConnectionError";
import warningsvg from "./../images/Icons/warning.svg";
import { useAuth } from "./../Context/Auth";

function Display(props) {
    const environment = props.environment;
    const urlParams = props.urlParams;

    const errorCount = props.errorCount;
    const setErrorCount = props.setErrorCount;
    const [stopList, setStopList] = useState([]);
    const [currentStop, setCurrentStop] = useState();
    const [currentRoute, setCurrentRoute] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [routeColours, setRouteColours] = useState({});
    const [stopCounter, setStopCounter] = useState(0);
    const [routeCounter, setRouteCounter] = useState(0);
    const [stopPredictionsList, setStopPredictionsList] = useState({});
    const { isPortrait, displayRecords, setDisplayRecords } = useAuth();

    useInterval(() => {
        //Load predictions once every 60 seconds (10 in test)
        loadPredictions();
    }, (["dev", "test"].includes(environment) ? 10 : 60) * 1000);

    useInterval(() => {
        //Set screen rotation to every 10 seconds (5 in dev)
        showNextScreen();
    }, (["dev"].includes(environment) ? 5 : 10) * 1000);

    useEffect(() => {
        //Run once the stops are loaded
        if (!isLoading && stopList.length) {
            loadPredictions();
            const warning = new Image();
            warning.src = warningsvg;
            //Assigned to unused state here so the image stays cached
        }
    }, [isLoading, stopList]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        //Run once the predictions are loaded
        if (
            Object.keys(stopPredictionsList).length > 0 &&
            !currentStop &&
            !currentRoute
        ) {
            setCurrentStop(stopList[0]);
            setCurrentRoute(stopList[0].routes[0]);
        }
    }, [stopPredictionsList, currentStop, currentRoute, stopList]);

    useEffect(() => {
        //Load the stops and routes
        setIsLoading(true);
        const stopIdList = urlParams.stops;
        //Promise that all the stops will been loaded into stopList
        Promise.all(
            stopIdList.map((stopId) => {
                //Create Promise to run both functions concurrently
                return Promise.all([
                    getStopInfo(stopId),
                    getStopRoutes(stopId),
                    getRouteColours(),
                ])
                    .then(([infoResponse, routesResponse, routeColours]) => {
                        //Put returned values where they need to be, and
                        //they will get filled as the Promises resolve
                        infoResponse.routes = routesResponse
                            .map((a) => a.RouteNo)
                            //Only return numeric values - we don't need 3a/6d/etc)
                            .filter((routeNo) => routeNo.length && !isNaN(routeNo));

                        infoResponse.trackerId = stopId;
                        setStopList((stopList) => [...stopList, infoResponse]);
                        setRouteColours(routeColours);
                    })
                    .catch((error) => {
                        setErrorCount((errorCount) => errorCount + 1);
                        console.log("Loading failed: " + error);
                    });
            })
        ).then(() => {
            setStopList((stopList) => {
                //The API doesn't link related stops, so we're going to do that here
                //Sort the stops so we are consistent between API loads
                const sortedStops = stopList.sort((a, b) =>
                    a.StopName.localeCompare(b.StopName)
                );
                //Loop through each item in stopList, adding them obj
                const groupedStops = sortedStops.reduce((obj, item) => {
                    const stopGroup = item.FlagStopNo + " " + item.StopName;
                    //If a group already exists then insert
                    if (obj[stopGroup]) {
                        //Add platforms stop platforms
                        obj[stopGroup].platforms.push(item);
                        //Add routes to stop routes
                        obj[stopGroup].routes = [
                            ...new Set([...obj[stopGroup].routes, ...item.routes]),
                        ];
                        //Sort the direction so to and from always display the same
                        obj[stopGroup].platforms = obj[stopGroup].platforms.sort((a, b) =>
                            a.CityDirection.localeCompare(b.CityDirection)
                        );
                    } else {
                        //Otherwise create a new group
                        obj[stopGroup] = {
                            platforms: [item],
                            routes: item.routes,
                        };
                    }
                    //Return an array-field version of the object
                    return obj;
                }, {});
                return Object.values(groupedStops);
            });
            setIsLoading(false);
        });
    }, [setErrorCount, urlParams.stops]);

    function loadPredictions() {
        //Here we need to grab the predictions for each route on each platform.
        //We don't really care about which platforms are at which stop, as each platform
        //has a unique ID so we can reference it directly. The Stop is just used for looping.
        Promise.all(
            stopList.map(async (stop) => {
                return Promise.all(
                    stop.platforms.map(async (platform) => {
                        const platPredictions = await getStopPredictions(platform.trackerId);
                        return [platform.trackerId, platPredictions];
                    })
                );
            })
        )
            .then((newPredictions) => {
                if (newPredictions.length > 0) {
                    setStopPredictionsList(Object.fromEntries(newPredictions.flat()));
                    setErrorCount(0);
                }
            })
            .catch((error) => {
                setErrorCount((errorCount) => errorCount + 1);
                console.log("new prediction error: " + error);
            });
    }

    function showNextScreen() {
        //TODO: Move this into external function and separate setting
        //of screen state from loop calculation
        var stop = stopCounter;
        var route = routeCounter;
        if (!isLoading && stopList.length) {
            if (route < stopList[stop].routes.length - 1) {
                route++;
            } else {
                if (stop < stopList.length - 1) {
                    route = 0;
                    stop++;
                } else {
                    route = 0;
                    stop = 0;
                }
            }
            setStopCounter(stop);
            setRouteCounter(route);
            setCurrentStop(stopList[stop]);
            setCurrentRoute(stopList[stop].routes[route]);
            let trackerId = "undefined";
            if (
                "platforms" in currentStop &&
                Array.isArray(currentStop.platforms) &&
                currentStop.platforms.length > 0 &&
                "trackerId" in currentStop.platforms[0]
            ) {
                trackerId = currentStop.platforms[0].trackerId;
            }
            if (typeof currentRoute !== "undefined") {
                const visitedNum = displayRecords[trackerId + currentRoute] || 0;
                setDisplayRecords({
                    ...displayRecords,
                    [trackerId + currentRoute]: (visitedNum + 1) % 6,
                });
            }
        }
    }

    if (errorCount < 1) {
        switch (
        isLoading ||
        !currentStop ||
        !currentStop.platforms ||
        !currentRoute ||
        !Object.keys(stopPredictionsList).length
        ) {
            case false:
                const platformPanels = currentStop.platforms.map((stopPlatform) => {
                    return (
                        <StopPredictions
                            stop={stopPlatform}
                            terminus={currentStop.platforms.length === 1}
                            routeNo={currentRoute}
                            routeColours={routeColours}
                            key={stopPlatform.trackerId}
                            stopPredictionsList={
                                stopPredictionsList[stopPlatform.trackerId][currentRoute]
                            }
                        />
                    );
                });
                return (
                    <TransitionGroup>
                        <CSSTransition
                            key={currentStop.platforms[0].trackerId}
                            timeout={300}
                            classNames="my-node"
                        >
                            <div className="grid-container">
                                <header className="header">
                                    <Branding />
                                    <div className="sub-header position-relative">
                                        <StopName stop={currentStop.platforms[0]} />
                                        <StopDirection stop={currentStop} />
                                        <ControlPanel />
                                    </div>
                                </header>

                                <main
                                    className={`tracker display-page ${isPortrait ? "portrait" : "landscape"
                                        }-mode`}
                                >
                                    {platformPanels}
                                </main>

                                <Footer
                                    currentRoute={currentRoute}
                                    routeList={currentStop.routes}
                                    routeColours={routeColours}
                                />
                            </div>
                        </CSSTransition>
                    </TransitionGroup>
                );
            default:
                return <LoadingScreen />;
        }
    } else {
        return <ConnectionError />;
    }
}

export default Display;
